import { useEffect, useRef, useState } from "react"
import { BereichGate } from "@/components/bereich-gesperrt"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { BookOpen } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { CoachBubble } from "@/components/coach-bubble"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { PruefungChatProvider } from "@/lib/pruefung-chat"
import { useCoachThread, usePruefungsLernfelder } from "@/lib/queries"
import { threadDestination } from "@/lib/recent-entries"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import {
  COACH_FALLBACK_LABEL,
  PRUEFUNG_ZIEL_STANDARD,
  PRUEFUNG_ZIEL_VORSCHLAEGE,
  type LernfeldStand,
} from "@nl/shared"
import { withoutQuestion } from "@/lib/chat-navigation"
import { pruefungStartPrompt, type CoachPrompt } from "@/lib/coach-prompts"

const OPTION_BADGE_CLASS =
  "border-[var(--rule)] bg-muted/20 text-[11.5px] font-normal text-muted-foreground"

/**
 * The opening of the drill, asked by the coach in the thread: first the area,
 * then the target. The answers are the chips above the input, like every
 * other answer in this conversation; see `einstiegsChips`.
 *
 * These bubbles are local and vanish once the opening message is sent; the
 * stored thread starts with it.
 */
function PruefungEinstieg({
  lernfeld,
  bereichAntwort,
  bereichGewaehlt,
}: {
  lernfeld?: LernfeldStand
  /** What she picked in the first step; unset when the area came with the link. */
  bereichAntwort?: string
  bereichGewaehlt: boolean
}) {
  const bereichFrage = (
    <CoachBubble
      role="assistant"
      text="Worauf willst du dich konzentrieren? Such dir ein Lernfeld aus oder lass mich quer durch alles fragen."
    />
  )
  if (!bereichGewaehlt) return bereichFrage

  return (
    <>
      {/* Arriving with an area already set (suggestion card in the open
          chat), nobody asked for it, so there is no answer to show. */}
      {bereichAntwort && (
        <>
          {bereichFrage}
          <CoachBubble role="user" text={bereichAntwort} />
        </>
      )}
      <CoachBubble
        role="assistant"
        text={`${bereichAntwort ? "" : lernfeld ? `Ich frag dich zu ${lernfeld.titel} ab. ` : "Ich frag dich quer durch alle Lernfelder ab. "}Wie viele Fragen nimmst du dir vor? Am Ende siehst du, wie viel richtig war, und kannst verlängern.`}
      />
    </>
  )
}

/**
 * The answers to whichever opening question is on screen.
 *
 * Nothing is sent until the target is picked: area and target go out
 * together in the first message, so the server stores the target with the
 * thread. "Alle" deliberately sets no target, so the run ends with the
 * verdict per area as before.
 */
function einstiegsChips(args: {
  lernfelder: LernfeldStand[]
  lernfeld?: LernfeldStand
  bereichGewaehlt: boolean
  onGemischt: () => void
  onLernfeld: (slug: string) => void
  onZurueck: () => void
  onZiel: (ziel: number | undefined) => void
}): CoachPrompt[] {
  if (!args.bereichGewaehlt) {
    return [
      { label: "Quer durch alles", aktion: args.onGemischt },
      ...args.lernfelder.map((l) => ({
        label: l.titel,
        aktion: () => args.onLernfeld(l.slug),
      })),
    ]
  }

  const fragenGesamt = args.lernfeld
    ? args.lernfeld.fragenGesamt
    : args.lernfelder.reduce((summe, l) => summe + l.fragenGesamt, 0)
  const starte =
    (ziel: number | undefined) => (ask: (text: string) => void) => {
      args.onZiel(ziel)
      ask(pruefungStartPrompt(args.lernfeld?.titel, ziel))
    }

  return [
    ...PRUEFUNG_ZIEL_VORSCHLAEGE.filter((n) => n < fragenGesamt).map((n) => ({
      label: `${n} Fragen`,
      aktion: starte(n),
      primaer: n === PRUEFUNG_ZIEL_STANDARD,
    })),
    ...(fragenGesamt > 0
      ? [{ label: `Alle ${fragenGesamt}`, aktion: starte(undefined) }]
      : []),
    { label: "Anderer Bereich", aktion: args.onZurueck },
    "Erklär mir erst den Stoff.",
    "Wo stehe ich?",
  ]
}

export function PruefungPage() {
  const { role } = useSessionContext()
  const navigate = useNavigate()
  const coach = useCoach()
  const coachName = coach?.name ?? COACH_FALLBACK_LABEL
  const {
    lernfeld: lernfeldSlug,
    gespraech,
    frage,
    gemischt: gemischtAusLink,
  } = Route.useSearch()
  const uebersicht = usePruefungsLernfelder()
  const { data: thread } = useCoachThread(gespraech)
  // A newly created empty thread receives its mode with the first message.
  const differentMode = Boolean(thread?.mode && thread.mode !== "pruefung")
  // A resume link without the focus area would clear the stored one on the
  // next turn (the API takes it from the request); pick it up from the thread.
  const missingLernfeld = Boolean(
    thread?.mode === "pruefung" && thread.lernfeld && !lernfeldSlug
  )
  useEffect(() => {
    if (!thread || !gespraech) return
    if (differentMode)
      void navigate({ ...threadDestination(thread), replace: true })
    else if (missingLernfeld)
      void navigate({
        to: "/pruefung",
        search: { lernfeld: thread.lernfeld, gespraech, frage },
        replace: true,
      })
  }, [thread, gespraech, differentMode, missingLernfeld, frage, navigate])

  // Gleiche Weiche wie im offenen Chat: das hier ist ein Beraterwerkzeug.
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])

  const lernfelder = uebersicht.data?.lernfelder ?? []
  const lernfeld = lernfelder.find((l) => l.slug === lernfeldSlug)
  const [gemischtGewaehlt, setGemischt] = useState(false)
  const gemischt = gemischtGewaehlt || Boolean(gemischtAusLink && !lernfeld)
  const [bereichAntwort, setBereichAntwort] = useState<string>()
  // A ref, not state: the first turn goes out in the same click that picks
  // the target, before a re-render could hand `extraBody` a new value. Later
  // turns keep sending it; the server only takes the first.
  const ziel = useRef<number | undefined>(undefined)

  if (differentMode || missingLernfeld) return null

  // Ohne Katalog gibt es nichts abzufragen, und ein Coach, der trotzdem
  // Fragen stellt, wäre genau das, was diese Funktion vermeiden soll.
  if (uebersicht.isSuccess && !lernfelder.length) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-[var(--rule)] bg-card p-5">
        <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-[14px] font-medium">
            Der Fragenkatalog ist noch nicht eingespielt.
          </p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">
            Sobald die Prüfungsunterlagen hinterlegt sind, fragt dich{" "}
            {coachName} hier entlang des Katalogs ab.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Kein Knopf zum Wechseln mehr: gewechselt wird im Gespräch, und es
          gibt keine Auswahlseite, auf die er zurückführen könnte. Das Badge
          bleibt, damit ein aus dem Chat übergebener Schwerpunkt sichtbar ist. */}
      {lernfeld && (
        <div className="mb-2 flex items-center gap-2">
          <Badge variant="outline" className={OPTION_BADGE_CLASS}>
            {lernfeld.titel}
          </Badge>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <PruefungChatProvider threadId={gespraech}>
          <GeneralCoachChat
            key={lernfeldSlug ?? "gemischt"}
            threadId={gespraech}
            initialPrompt={frage}
            onInitialPromptTaken={() =>
              void navigate({
                to: "/pruefung",
                search: withoutQuestion,
                replace: true,
              })
            }
            mode="pruefung"
            // Only the subject area travels. The graded answer is read by the
            // server from the thread, so nothing here can go missing between
            // the card and the turn.
            extraBody={() => ({ lernfeld: lernfeldSlug, ziel: ziel.current })}
            onThreadCreated={(id) =>
              void navigate({
                to: "/pruefung",
                search: { lernfeld: lernfeldSlug, gespraech: id },
                replace: true,
              })
            }
            onReset={() =>
              void navigate({
                to: "/pruefung",
                search: { lernfeld: lernfeldSlug },
                replace: true,
              })
            }
            empty={{
              // The greeting stays general; what to drill and how much is
              // asked in the bubbles below it.
              title: lernfeld
                ? `${coachName} fragt dich zu ${lernfeld.titel} ab.`
                : `Mit ${coachName} für die IVFP-Prüfung üben`,
              hint: "Ich stelle dir Fragen aus der IVFP-Prüfung. Du antwortest direkt auf der Karte, danach erkläre ich dir den Lösungsweg.",
              intro: (
                <PruefungEinstieg
                  lernfeld={lernfeld}
                  bereichAntwort={bereichAntwort}
                  bereichGewaehlt={Boolean(lernfeld) || gemischt}
                />
              ),
              prompts: einstiegsChips({
                lernfelder,
                lernfeld,
                bereichGewaehlt: Boolean(lernfeld) || gemischt,
                onGemischt: () => {
                  setGemischt(true)
                  setBereichAntwort("Quer durch alles")
                },
                onLernfeld: (slug) => {
                  setBereichAntwort(
                    lernfelder.find((l) => l.slug === slug)?.titel
                  )
                  void navigate({ to: "/pruefung", search: { lernfeld: slug } })
                },
                onZurueck: () => {
                  setGemischt(false)
                  setBereichAntwort(undefined)
                  if (lernfeldSlug || gemischtAusLink)
                    void navigate({ to: "/pruefung", search: {} })
                },
                onZiel: (n) => {
                  ziel.current = n
                },
              }),
            }}
            placeholder="Deine Antwort …"
          />
        </PruefungChatProvider>
      </div>
    </div>
  )
}

function PruefungPageGesperrt() {
  const { gespraech } = Route.useSearch()
  // An existing conversation stays reachable; the lock only stops a new one.
  return (
    <BereichGate bereich="pruefungsvorbereitung" offen={Boolean(gespraech)}>
      <PruefungPage />
    </BereichGate>
  )
}

export const Route = createFileRoute("/_app/pruefung")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    lernfeld?: string
    gespraech?: string
    frage?: string
    gemischt?: boolean
  } => ({
    lernfeld:
      typeof search.lernfeld === "string" && search.lernfeld.trim()
        ? search.lernfeld.trim()
        : undefined,
    gespraech:
      typeof search.gespraech === "string" && search.gespraech.trim()
        ? search.gespraech.trim()
        : undefined,
    // Sent as the opening message, the way `/chat?frage=` works. The tour
    // uses it to have the coach ask a first catalogue question.
    frage:
      typeof search.frage === "string" && search.frage.trim()
        ? search.frage.trim()
        : undefined,
    // The area is already settled as "all of it" (a suggestion card without
    // a focus), so the coach asks only for the target.
    gemischt:
      search.gemischt === true || search.gemischt === "true" ? true : undefined,
  }),
  component: PruefungPageGesperrt,
})
